import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shirt, Tv, Coffee, Megaphone, Sparkles, Trophy, Download,
  Check, ArrowRight, Star, FileText, Loader2,
} from "lucide-react";

const PRIMARY = "#4F1B7C";
const SECONDARY = "#EC1F7C";
const BG_DEEP = "#280F4F";
const BROCHURE_URL = "https://customer-assets.emergentagent.com/job_pod-istres-hb/artifacts/6baiabou_Istres%20Provence%20Handball%20-%2025_26.pdf";

// =========================================================
// Pricing data extracted from the official 25/26 brochure
// =========================================================
const TABS = [
  {
    id: "club",
    label: "Business Club",
    icon: Star,
    desc: "Devenez membre du Business Club et profitez de l'expérience VIP toute la saison.",
    offers: [
      { name: "Pack Starter", price: "2 000", places: 4, popular: false, perks_count: 12 },
      { name: "Pack 7 Majeur", price: "3 100", places: 4, popular: false, perks_count: 12 },
      { name: "Pack Élite", price: "4 100", places: 6, popular: true, perks_count: 12 },
      { name: "Pack All Star", price: "5 100", places: 8, popular: false, perks_count: 12 },
      { name: "Pack Legend", price: "6 100", places: 8, popular: false, perks_count: 12 },
      { name: "Pack Icone", price: "8 100", places: 10, popular: false, perks_count: 12 },
    ],
    features: [
      "Places en tribune VIP",
      "Accès Espace Premium (avant-match, mi-temps, après-match)",
      "Parking VIP",
      "Diffusion sur LED partenaire général",
      "Soirée présentation effectif pro (2 pers.)",
      "Soirée des vœux du Président (2 pers.)",
      "Soirée de fin de saison (2 pers.)",
      "Inscription au tournoi partenaire",
      "Annonce sur réseaux sociaux & newsletter",
      "Mise en avant en présence de joueurs pros",
      "Entrainement découverte handball animé par un coach",
      "Billets Grand Public supplémentaires",
    ],
  },
  {
    id: "hospitality",
    label: "Hospitalité",
    icon: Coffee,
    desc: "Recevez vos clients dans nos espaces premium et créez des moments mémorables.",
    offers: [
      { name: "Loge Privée Annuelle", price: "14 900", desc: "10 personnes · service traiteur · saison complète", popular: true },
      { name: "Loge Privée par match", price: "2 500", desc: "10 personnes · service traiteur · 1 match" },
      { name: "Parrain de match", price: "2 000", desc: "Coup d'envoi + 12 LED + 10 places Espace Premium" },
    ],
  },
  {
    id: "jersey",
    label: "Affichage Maillot",
    icon: Shirt,
    desc: "Diffusez votre marque en BeIN Sports & Handball TV — visibilité nationale toute la saison.",
    sub_groups: [
      {
        title: "Maillot Pro — Devant",
        offers: [
          { name: "Poitrine", price: "55 000", popular: true },
          { name: "Ventre", price: "50 000" },
          { name: "Ventre Bas", price: "40 000" },
          { name: "Trapèze (par emplacement)", price: "35 000" },
          { name: "Manche Droite", price: "25 000" },
        ],
      },
      {
        title: "Maillot Pro — Dos & Cuisses",
        offers: [
          { name: "Nuque", price: "35 000" },
          { name: "Dos", price: "25 000" },
          { name: "Dos Bas", price: "15 000" },
          { name: "Cuisse avant", price: "15 000" },
          { name: "Cuisse arrière", price: "15 000" },
          { name: "Fesses", price: "15 000" },
        ],
      },
      {
        title: "Maillot Échauffement & Tenue Staff",
        offers: [
          { name: "Échauffement — Poitrine", price: "8 000" },
          { name: "Échauffement — Dos", price: "7 000" },
          { name: "Tenue Match Entraîneur", price: "6 000" },
          { name: "Veste d'échauffement", price: "6 000" },
          { name: "T-Shirt Serpilleros", price: "2 000" },
        ],
      },
      {
        title: "Équipes Jeunes",
        offers: [
          { name: "Maillot U9 / U11 / U13 / U15", price: "1 000" },
          { name: "Maillot U17 / U18F / N2", price: "2 500" },
        ],
      },
    ],
  },
  {
    id: "visibility",
    label: "Visibilité Terrain",
    icon: Tv,
    desc: "Captez l'attention caméra : LED bord de terrain, stickers, écran géant, bâches, panneaux.",
    sub_groups: [
      {
        title: "LED & Écrans",
        offers: [
          { name: "Affichage LED 80m (6 × 15s)", price: "8 000", popular: true },
          { name: "Affichage LED 40m (6 × 15s)", price: "6 000" },
          { name: "Affichage LED 20m (6 × 15s)", price: "5 000" },
          { name: "Écran Géant — saison (Avant + Mi-Temps + Temps Mort)", price: "20 000" },
          { name: "Écran Géant — saison (Temps Mort 20s)", price: "15 000" },
          { name: "Écran Géant — saison (Avant + Mi-Temps 30s)", price: "10 000" },
          { name: "Écran Géant — par match", price: "2 000" },
        ],
      },
      {
        title: "Stickers & Bâches Terrain",
        offers: [
          { name: "Méga bâche arrière but × 2", price: "30 000", popular: true },
          { name: "Méga bâche arrière but × 1", price: "18 000" },
          { name: "Arche entrée joueurs", price: "17 000" },
          { name: "Sticker Zone (450×130 cm)", price: "8 000" },
          { name: "Sticker Terrain", price: "7 000" },
          { name: "Sticker Bord de Terrain", price: "6 000" },
          { name: "Panneautique Corner (à l'unité)", price: "6 000" },
          { name: "Cube pub Serpilleros × 4", price: "4 000" },
          { name: "Panneau arrière LED (stickage)", price: "1 200" },
        ],
      },
    ],
  },
  {
    id: "communication",
    label: "Communication",
    icon: Megaphone,
    desc: "Multipliez vos canaux : print, digital, vidéo, réseaux sociaux.",
    offers: [
      { name: "Pack Logo (affiches + e-billets + programme)", price: "12 000", popular: true },
      { name: "Action communication avec joueurs pros", price: "2 000" },
      { name: "Trophée Joueur du Mois", price: "1 500" },
      { name: "Stories jour de match", price: "500" },
      { name: "Encart Programme de match", price: "400" },
      { name: "Encart Newsletter", price: "400" },
      { name: "Parrainage résumé vidéo (par match)", price: "400" },
      { name: "Post sponsorisé", price: "Sur demande" },
      { name: "Naming pellicule de match", price: "Sur demande" },
    ],
  },
  {
    id: "events",
    label: "Événementiel",
    icon: Sparkles,
    desc: "Créez des moments uniques pour vos collaborateurs et clients.",
    offers: [
      { name: "Maillot Mascotte logo ventre", price: "5 000", popular: true },
      { name: "Maillot Mascotte logo dos", price: "4 000" },
      { name: "Naming animation mi-temps (par match)", price: "2 000" },
      { name: "Journée cohésion (initiation hand + déjeuner)", price: "Sur demande" },
      { name: "Conférence sur-mesure", price: "Sur demande" },
      { name: "Soirée locaux partenaire avec effectif pro", price: "Sur demande" },
      { name: "Clap clap supporters (saison)", price: "Sur demande" },
    ],
  },
];

// =========================================================
// MAIN COMPONENT
// =========================================================
export default function BusinessPricingTabs({ onCta }) {
  const [activeId, setActiveId] = useState("club");
  const active = useMemo(() => TABS.find((t) => t.id === activeId), [activeId]);

  return (
    <section id="prestations" className="relative py-20 sm:py-24 overflow-hidden" style={{ background: BG_DEEP }}>
      {/* halftone deco */}
      <div className="absolute inset-0 opacity-15 pointer-events-none" style={{
        backgroundImage: `radial-gradient(circle, ${SECONDARY} 1px, transparent 1px)`,
        backgroundSize: "16px 16px",
        maskImage: "radial-gradient(ellipse at center, black, transparent 75%)",
      }}/>

      <div className="relative max-w-7xl mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 max-w-3xl mx-auto">
          <div className="text-pink-400 font-black tracking-[0.3em] text-xs uppercase mb-3">Prestations 2025-2026</div>
          <h2 className="text-white font-black text-3xl sm:text-5xl uppercase italic">
            Toutes nos <span style={{ color: SECONDARY }}>offres business</span>
          </h2>
          <p className="text-white/75 mt-4">Choisissez votre catégorie, découvrez les tarifs officiels — toutes les offres sont issues de notre plaquette commerciale 25/26.</p>
        </div>

        {/* Brochure download CARD */}
        <BrochureCard/>

        {/* TABS */}
        <div className="mt-14 flex flex-wrap justify-center gap-2 mb-10">
          {TABS.map((t) => {
            const isActive = activeId === t.id;
            return (
              <button key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setActiveId(t.id)}
                className={`relative inline-flex items-center gap-2 px-5 py-3 rounded-full font-black uppercase text-[11px] tracking-widest transition-all ${isActive ? "scale-105 text-white shadow-[0_8px_24px_rgba(236,31,124,0.5)]" : "text-white/70 hover:text-white border border-white/15 hover:border-white/30"}`}
                style={{ background: isActive ? `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` : "rgba(255,255,255,0.04)" }}>
                <t.icon size={14}/>
                {t.label}
              </button>
            );
          })}
        </div>

        {/* TAB CONTENT */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeId}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.35 }}
          >
            <div className="text-center mb-8 max-w-2xl mx-auto">
              <h3 className="text-white font-black text-xl sm:text-2xl uppercase italic">{active.label}</h3>
              <p className="text-white/70 text-sm mt-2">{active.desc}</p>
            </div>

            {/* Business club special layout */}
            {active.id === "club" ? (
              <ClubPacksLayout active={active} onCta={onCta}/>
            ) : active.sub_groups ? (
              <div className="space-y-10">
                {active.sub_groups.map((group, gi) => (
                  <div key={group.title}>
                    <div className="text-pink-400 font-black tracking-[0.3em] text-[11px] uppercase mb-4">{group.title}</div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {group.offers.map((o, i) => <PriceCard key={o.name} offer={o} index={gi * 10 + i} onCta={onCta}/>)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {active.offers.map((o, i) => <PriceCard key={o.name} offer={o} index={i} onCta={onCta}/>)}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}

// =========================================================
// CLUB PACKS LAYOUT
// =========================================================
function ClubPacksLayout({ active, onCta }) {
  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {active.offers.map((p, i) => (
          <motion.div key={p.name}
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
            className={`relative rounded-3xl overflow-hidden ${p.popular ? "ring-4 ring-pink-400 lg:scale-105" : ""}`}
            data-testid={`pack-${p.name.toLowerCase().replace(/\s+/g,"-")}`}>
            {p.popular && (
              <div className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-pink-500 text-white text-[10px] font-black uppercase tracking-widest shadow-lg">
                <Star size={10} fill="white"/> Populaire
              </div>
            )}
            <div className="px-6 py-7 text-center" style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #2c0e4a 100%)` }}>
              <h4 className="text-white font-black text-xl uppercase italic">{p.name}</h4>
              <div className="mt-3 text-white">
                <span className="font-black text-pink-400" style={{ fontSize: "2.5rem" }}>{p.price}</span>
                <span className="text-pink-300 font-black text-2xl">€</span>
                <div className="text-white/70 text-[10px] font-bold uppercase tracking-widest mt-1">HT / saison</div>
              </div>
              <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-white text-xs font-black uppercase tracking-widest">
                {p.places} places VIP
              </div>
            </div>
            <div className="bg-white p-5">
              <div className="text-pink-600 text-[10px] font-black uppercase tracking-widest mb-2">Inclus dans tous les packs</div>
              <ul className="space-y-1.5 text-[12px] text-slate-700">
                {active.features.slice(0, 6).map((f) => (
                  <li key={f} className="flex items-start gap-1.5"><Check size={11} className="text-emerald-500 mt-0.5 shrink-0"/> {f}</li>
                ))}
              </ul>
              <details className="mt-2">
                <summary className="text-pink-600 text-[11px] font-black cursor-pointer">+ {active.features.length - 6} avantages</summary>
                <ul className="mt-2 space-y-1.5 text-[12px] text-slate-700">
                  {active.features.slice(6).map((f) => (
                    <li key={f} className="flex items-start gap-1.5"><Check size={11} className="text-emerald-500 mt-0.5 shrink-0"/> {f}</li>
                  ))}
                </ul>
              </details>
              <button onClick={() => onCta?.(p.name)}
                data-testid={`pack-cta-${p.name.toLowerCase().replace(/\s+/g,"-")}`}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-black uppercase text-[11px] tracking-widest text-white shadow-lg hover:scale-[1.02] transition"
                style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
                Choisir ce pack <ArrowRight size={12}/>
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </>
  );
}

// =========================================================
// PRICE CARD (used for individual visibility / communication offers)
// =========================================================
function PriceCard({ offer, index, onCta }) {
  const onDemand = (offer.price + "").toLowerCase().includes("demande");
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.04 }}
      className={`relative rounded-2xl overflow-hidden bg-white shadow-lg hover:shadow-2xl transition group ${offer.popular ? "ring-2 ring-pink-400" : ""}`}>
      {offer.popular && (
        <div className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-pink-500 text-white text-[9px] font-black uppercase tracking-widest shadow-lg">
          <Star size={8} fill="white"/> Top
        </div>
      )}
      <div className="p-4 sm:p-5">
        <h4 className="font-black uppercase text-sm leading-tight" style={{ color: PRIMARY }}>{offer.name}</h4>
        {offer.desc && <p className="text-slate-500 text-[11px] mt-1">{offer.desc}</p>}
        <div className="mt-3 flex items-end justify-between">
          <div>
            {onDemand ? (
              <div className="font-black text-base text-slate-700 italic">Sur demande</div>
            ) : (
              <>
                <span className="font-black text-2xl" style={{ color: SECONDARY }}>{offer.price}</span>
                <span className="text-pink-500 font-black text-base"> €</span>
                <div className="text-slate-400 text-[9px] uppercase tracking-widest font-bold">HT / saison</div>
              </>
            )}
          </div>
          <button onClick={() => onCta?.(offer.name)}
            data-testid={`offer-cta-${offer.name.toLowerCase().replace(/[^a-z0-9]+/g,"-").slice(0, 30)}`}
            className="opacity-90 group-hover:opacity-100 inline-flex items-center justify-center w-9 h-9 rounded-full text-white shadow-lg hover:scale-110 transition"
            style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}
            aria-label={`Demander un devis pour ${offer.name}`}>
            <ArrowRight size={14}/>
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// =========================================================
// BROCHURE DOWNLOAD CARD with animation
// =========================================================
function BrochureCard() {
  const [downloading, setDownloading] = useState(false);
  const [done, setDone] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setDone(false);
    setProgress(0);

    // Simulated progress while we fetch
    const tick = setInterval(() => {
      setProgress((p) => Math.min(p + 5 + Math.random() * 8, 92));
    }, 120);

    try {
      const r = await fetch(BROCHURE_URL);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Istres_Provence_Handball_Brochure_2025-2026.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setProgress(100);
      setDone(true);
      setTimeout(() => { setDone(false); setDownloading(false); setProgress(0); }, 2200);
    } catch (e) {
      // Fallback : open the PDF in a new tab
      window.open(BROCHURE_URL, "_blank", "noopener,noreferrer");
      setDownloading(false);
      setProgress(0);
    } finally {
      clearInterval(tick);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="relative max-w-4xl mx-auto rounded-3xl overflow-hidden shadow-[0_25px_70px_rgba(236,31,124,0.35)]"
      data-testid="brochure-card"
    >
      <div className="grid sm:grid-cols-[200px,1fr] gap-0 items-stretch"
        style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #2c0e4a 100%)` }}>
        {/* Cover thumbnail */}
        <div className="relative h-56 sm:h-auto overflow-hidden flex items-center justify-center p-5"
          style={{ background: `linear-gradient(160deg, ${SECONDARY} 0%, ${PRIMARY} 100%)` }}>
          {/* Faux PDF cover */}
          <motion.div
            initial={{ rotate: -3, scale: 1 }}
            animate={{ rotate: [-3, 3, -3] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="relative w-32 sm:w-40 aspect-[1/1.41] rounded-md shadow-2xl bg-white overflow-hidden"
          >
            <div className="absolute inset-0 flex flex-col items-center justify-center p-3 text-center" style={{ background: "linear-gradient(180deg, #4F1B7C 0%, #160732 100%)" }}>
              <FileText size={28} className="text-pink-400 mb-2"/>
              <div className="text-white font-black uppercase italic text-[10px] leading-tight">Istres Provence Handball</div>
              <div className="text-pink-400 font-black tracking-widest text-[8px] mt-1">2025 / 2026</div>
              <div className="mt-2 text-white/70 text-[8px]">Brochure commerciale</div>
              <div className="mt-3 w-12 h-0.5 bg-pink-400"/>
              <div className="mt-2 text-white/50 text-[7px]">PDF · ~2 MB</div>
            </div>
            {/* paper corners shine */}
            <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.18) 0%, transparent 30%)" }}/>
          </motion.div>
        </div>

        {/* Right side : text + CTA */}
        <div className="p-6 sm:p-8 flex flex-col justify-center">
          <div className="text-pink-400 font-black tracking-[0.3em] text-[10px] uppercase mb-2">Plaquette commerciale officielle</div>
          <h3 className="text-white font-black text-xl sm:text-2xl uppercase italic leading-tight">
            Téléchargez la brochure <span style={{ color: SECONDARY }}>2025/2026</span>
          </h3>
          <p className="text-white/75 text-sm mt-3">Toutes nos offres détaillées : packs Business Club, hospitalité, affichage maillot, visibilité terrain, communication, événementiel.</p>

          {/* Progress bar */}
          {(downloading || done) && (
            <div className="mt-4 w-full h-2 bg-white/10 rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full"
                style={{ background: `linear-gradient(90deg, ${SECONDARY}, #ff3a8c)` }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          )}

          <button
            data-testid="brochure-download-btn"
            onClick={handleDownload}
            disabled={downloading}
            className={`mt-5 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-black uppercase text-xs tracking-widest text-white shadow-lg transition ${downloading ? "cursor-not-allowed" : "hover:scale-105"}`}
            style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}
          >
            {done ? (
              <>
                <Check size={14}/> Téléchargé !
              </>
            ) : downloading ? (
              <>
                <Loader2 size={14} className="animate-spin"/> {Math.round(progress)}%
              </>
            ) : (
              <>
                <Download size={14}/> Télécharger la plaquette PDF
              </>
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
